import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import {
  ChevronLeft, ChevronRight, Sparkles, Loader2,
  Pencil, Eye, RefreshCw, Save
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import { checkExportEntitlement, injectExportBranding } from '../utils/exportEntitlement';

import { themes, sizeConfigs, pageTypeMeta } from './product-view/themes';
import ThemeSelector from './product-view/ThemeSelector';
import ExportPanel from './product-view/ExportPanel';
import SaveTemplateModal from './product-view/SaveTemplateModal';
import DocumentCover from './product-view/DocumentCover';
import DocumentPage from './product-view/DocumentPage';
import PageEditPanel from './product-view/PageEditPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TEMPLATE_CATEGORIES = ['planner', 'guide', 'workbook', 'tracker', 'checklist', 'other'];

const DocumentProductView = ({ content, productType, onSave, onNew, saving, onUpdateContent, onEditSettings, projectId, outputSize, readOnly = false, productSlug = null }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState('modern-minimal');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [regeneratingPage, setRegeneratingPage] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const pageRef = useRef(null);

  if (!content) return null;

  const pages = content.pages || [];
  const totalPages = pages.length + 1;

  const handlePrev = () => setCurrentPage(p => Math.max(0, p - 1));
  const handleNext = () => setCurrentPage(p => Math.min(totalPages - 1, p + 1));

  /* ─── Edit functions ─── */
  const handlePageUpdate = (pageIndex, updatedPage) => {
    const newPages = [...pages];
    newPages[pageIndex] = updatedPage;
    onUpdateContent({ ...content, pages: newPages });
    setHasUnsavedChanges(true);
  };

  const handleDeletePage = (pageIndex) => {
    const newPages = pages.filter((_, i) => i !== pageIndex);
    onUpdateContent({ ...content, pages: newPages });
    setHasUnsavedChanges(true);
    if (currentPage > newPages.length) setCurrentPage(newPages.length);
  };

  const handleRegeneratePage = async (pageIndex) => {
    setRegeneratingPage(pageIndex);
    try {
      const res = await axios.post(`${API}/content/regenerate-page`, {
        product_title: content.title, product_type: productType,
        page_index: pageIndex, existing_page: pages[pageIndex],
        context_summary: content.instructions || '', language: 'en', tone: 'friendly'
      });
      handlePageUpdate(pageIndex, res.data);
    } catch (err) { console.error('Regenerate page failed:', err); }
    setRegeneratingPage(null);
  };

  const handleSaveChanges = async () => {
    if (!projectId || !onSave) return;
    setSavingChanges(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, { content, title: content.title });
      setHasUnsavedChanges(false);
    } catch (err) { console.error('Save changes failed:', err); }
    setSavingChanges(false);
  };

  /* ─── Export functions ─── */
  const doExport = async (format) => {
    setExporting(true);
    setExportProgress(0);
    const prevEdit = editMode;
    setEditMode(false);
    await new Promise(r => setTimeout(r, 100));

    // Server-side entitlement check
    const entitlement = await checkExportEntitlement(productSlug);
    const shouldBrand = !entitlement.clean;

    try {
      const cfg = sizeConfigs[outputSize] || sizeConfigs['a4-document'];
      const isPDF = format === 'pdf';
      const pdf = isPDF ? new jsPDF({ orientation: cfg.height > cfg.width ? 'portrait' : 'landscape', unit: 'px', format: [cfg.width, cfg.height], compress: true }) : null;

      for (let i = 0; i < totalPages; i++) {
        setCurrentPage(i);
        setExportProgress(Math.round(((i + 1) / totalPages) * 100));
        await new Promise(r => setTimeout(r, 400));
        const el = pageRef.current;
        if (!el) continue;

        // Inject branding for free users
        let removeBranding = null;
        if (shouldBrand) {
          removeBranding = injectExportBranding(el);
          await new Promise(r => setTimeout(r, 50));
        }

        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null });

        // Remove branding immediately after capture
        if (removeBranding) removeBranding();

        if (isPDF) {
          const targetW = cfg.width * 2;
          const targetH = cfg.height * 2;
          let finalCanvas = canvas;
          if (canvas.width > targetW || canvas.height > targetH) {
            const offscreen = document.createElement('canvas');
            offscreen.width = targetW;
            offscreen.height = targetH;
            const ctx = offscreen.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(canvas, 0, 0, targetW, targetH);
            finalCanvas = offscreen;
          }
          const imgData = finalCanvas.toDataURL('image/jpeg', 0.85);
          if (i > 0) pdf.addPage([cfg.width, cfg.height]);
          pdf.addImage(imgData, 'JPEG', 0, 0, cfg.width, cfg.height, undefined, 'FAST');
        } else {
          const link = document.createElement('a');
          link.download = `${content.title || 'document'}-page-${i + 1}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      }
      if (isPDF) pdf.save(`${content.title || 'document'}.pdf`);
    } catch (err) { console.error(`${format.toUpperCase()} export error:`, err); }
    setEditMode(prevEdit);
    setExporting(false);
  };

  return (
    <div data-testid="document-product-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">
            {readOnly ? content.title || 'Shared product' : 'Your product is ready!'}
          </h2>
          {!readOnly && <p className="text-stone-600 font-sans mt-1">Preview, edit and export your {productType?.replace(/-/g, ' ') || 'document'}.</p>}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center bg-stone-100 rounded-full p-1">
              <button data-testid="doc-preview-toggle" onClick={() => setEditMode(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-semibold transition-all ${!editMode ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}>
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
              <button data-testid="doc-edit-toggle" onClick={() => setEditMode(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-semibold transition-all ${editMode ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
            {hasUnsavedChanges && projectId && (
              <Button data-testid="doc-save-changes-btn" onClick={handleSaveChanges} disabled={savingChanges} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 h-9 text-xs font-sans font-semibold">
                {savingChanges ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save Changes
              </Button>
            )}
            <Button data-testid="doc-save-btn" onClick={onSave} disabled={saving} className="bg-accent text-white hover:bg-accent/90 rounded-full px-5 font-sans font-semibold h-9 text-xs">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
            <Button data-testid="doc-save-template-btn" onClick={() => setShowSaveTemplate(true)} variant="outline" className="rounded-full px-4 h-9 text-xs font-sans font-semibold border-stone-200 text-stone-600 hover:border-primary hover:text-primary">
              <Sparkles className="w-3 h-3 mr-1" /> Save as template
            </Button>
            <Button data-testid="doc-new-btn" variant="ghost" onClick={onNew} className="text-stone-600 font-sans h-9 text-xs">Create new</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <ThemeSelector selectedTheme={selectedTheme} onSelectTheme={setSelectedTheme} />
            {onEditSettings && (
              <Button data-testid="doc-edit-settings-btn" onClick={onEditSettings} variant="outline" className="h-8 px-3 text-xs font-sans font-semibold rounded-full border-stone-200 text-stone-600 hover:border-primary hover:text-primary">
                <RefreshCw className="w-3 h-3 mr-1" /> Regenerate all
              </Button>
            )}
          </div>

          {/* Page preview */}
          <div ref={pageRef} className="w-full max-w-[420px] mx-auto rounded-2xl overflow-hidden shadow-lg border border-stone-200/50">
            {currentPage === 0
              ? <DocumentCover cover={content.cover} title={content.title} theme={selectedTheme} />
              : <DocumentPage page={pages[currentPage - 1]} pageIndex={currentPage - 1} totalPages={pages.length} theme={selectedTheme} />}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4">
            <Button variant="ghost" onClick={handlePrev} disabled={currentPage === 0} className="text-stone-500 rounded-full w-10 h-10 p-0"><ChevronLeft className="w-5 h-5" /></Button>
            <span className="text-sm font-sans font-medium text-stone-500">{currentPage === 0 ? 'Cover' : `Page ${currentPage}`} / {pages.length} pages</span>
            <Button variant="ghost" onClick={handleNext} disabled={currentPage === totalPages - 1} className="text-stone-500 rounded-full w-10 h-10 p-0"><ChevronRight className="w-5 h-5" /></Button>
          </div>

          {/* Thumbnails */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 px-1">
            {Array.from({ length: totalPages }).map((_, i) => {
              const t = themes[selectedTheme];
              const isCover = i === 0;
              return (
                <button key={i} onClick={() => setCurrentPage(i)}
                  className={`flex-shrink-0 w-10 h-14 rounded-md border-2 overflow-hidden transition-all ${currentPage === i ? 'border-primary scale-110 shadow-md' : 'border-stone-200 opacity-60 hover:opacity-100'}`}>
                  <div className="w-full h-full flex items-center justify-center text-[7px] font-bold" style={{ backgroundColor: isCover ? t.accent : t.cardBg, color: isCover ? t.bg : t.text }}>
                    {isCover ? 'Cover' : i}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Edit panels */}
          {editMode && currentPage > 0 && pages[currentPage - 1] && (
            <PageEditPanel
              page={pages[currentPage - 1]} pageIndex={currentPage - 1}
              onUpdate={handlePageUpdate} onRegenerate={handleRegeneratePage}
              onDelete={handleDeletePage} regenerating={regeneratingPage === currentPage - 1}
              totalPages={pages.length}
            />
          )}
          {editMode && currentPage === 0 && (
            <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
              <h4 className="font-sans font-bold text-stone-800 text-sm">Edit Cover</h4>
              <div>
                <label className="text-xs font-sans font-medium text-stone-500 mb-1 block">Title</label>
                <input data-testid="edit-cover-title" value={content.cover?.title || content.title || ''}
                  onChange={(e) => { onUpdateContent({ ...content, cover: { ...(content.cover || {}), title: e.target.value }, title: e.target.value }); setHasUnsavedChanges(true); }}
                  className="w-full text-sm font-sans font-semibold text-stone-800 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" />
              </div>
              <div>
                <label className="text-xs font-sans font-medium text-stone-500 mb-1 block">Subtitle</label>
                <input data-testid="edit-cover-subtitle" value={content.cover?.subtitle || ''}
                  onChange={(e) => { onUpdateContent({ ...content, cover: { ...(content.cover || {}), subtitle: e.target.value } }); setHasUnsavedChanges(true); }}
                  className="w-full text-sm font-sans text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" />
              </div>
            </div>
          )}
        </div>

        {/* Right: Export & Info */}
        {!readOnly && (
          <>
            <div className="space-y-6">
              <ExportPanel
                onExportPNG={() => doExport('png')} onExportPDF={() => doExport('pdf')}
                exporting={exporting} exportProgress={exportProgress}
                totalCount={totalPages} outputSize={outputSize}
                variant="document"
                productTitle={content.cover?.title || productType || ''}
                productDescription={content.cover?.subtitle || content.instructions || ''}
                productType={productType || 'Digital Download'}
                productSlug={productSlug}
                projectId={projectId}
                themeId={selectedTheme}
              />
              {content.instructions && (
                <div className="bg-white rounded-2xl border border-stone-100 p-6">
                  <h3 className="font-sans font-bold text-stone-800 mb-3">How to use it</h3>
                  <p className="text-sm font-sans text-stone-600 leading-relaxed">{content.instructions}</p>
                </div>
              )}
              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <h3 className="font-sans font-bold text-stone-800 mb-3">All pages</h3>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {pages.map((page, i) => {
                    const meta = pageTypeMeta[page.pageType] || { label: 'Page', color: '#A0A0A0' };
                    return (
                      <button key={i} onClick={() => setCurrentPage(i + 1)}
                        className={`w-full text-left p-3 rounded-lg border transition-all text-sm ${currentPage === i + 1 ? 'border-primary bg-primary/5' : 'border-stone-100 hover:border-stone-200'}`}>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full" style={{ backgroundColor: meta.color + '18', color: meta.color }}>{meta.label}</span>
                          <span className="text-[10px] text-stone-400">Page {i + 1}</span>
                        </div>
                        <p className="font-sans font-medium text-stone-700 leading-snug">{page.title}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <SaveTemplateModal
              open={showSaveTemplate}
              onClose={() => setShowSaveTemplate(false)}
              content={content}
              productType={productType || 'planner'}
              categories={DOC_TEMPLATE_CATEGORIES}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentProductView;
